import React, { useRef, useEffect } from "react";
import PropTypes from "prop-types";
import { Swiper, SwiperSlide } from "swiper/react";
import { FreeMode, Navigation, Pagination, Scrollbar } from "swiper/modules";
import { LazyLoadImage } from "react-lazy-load-image-component";
import ImagePlaceholder from "components/common/ImagePlaceholder";
import Icon from "components/common/Icon";
import styles from "./PhotoSlider.module.css";

const PhotoSlider = ({ photos = [], cropped_photos = [], preview = false, ratio = "4x3", slidesPerView = 1, className, swiperClassName, ...rest }) => {
  // Combine photos and cropped photos
  const allPhotos = [
    ...photos.map(photo => ({ ...photo, type: 'photo' })),
    ...cropped_photos.map(photo => ({ ...photo, type: 'cropped_photos' }))
  ];

  // Sort photos with position 0 first
  const sortedPhotos = allPhotos.toSorted((a, b) => {
    if (a.position === 0 && b.position !== 0) return -1;
    if (b.position === 0 && a.position !== 0) return 1;
    return 0;
  });
  const slideLeftRef = useRef(null);
  const slideRightRef = useRef(null);
  const swiperRef = useRef(null);

  useEffect(() => {
    if (swiperRef.current) {
      swiperRef.current.params.navigation.prevEl = slideLeftRef.current;
      swiperRef.current.params.navigation.nextEl = slideRightRef.current;
      swiperRef.current.navigation.init();
      swiperRef.current.navigation.update();
    }
  }, [swiperRef.current, slideLeftRef.current, slideRightRef.current]);

  const classes = ["mw-100"];
  if (className) classes.push(className);

  const swiperClasses = ["hover-target bg-gray-lightest h-100"];
  if (swiperClassName) swiperClasses.push(swiperClassName);

  return (
    <div className={classes.join(" ")} {...rest}>
      <Swiper
        onSwiper={(swiper) => (swiperRef.current = swiper)}
        slidesPerView={slidesPerView}
        spaceBetween={slidesPerView > 1 ? 8 : 0}
        modules={[FreeMode, Scrollbar, Navigation, Pagination]}
        pagination={{ horizontalClass: "bottom-0" }}
        className={swiperClasses.join(" ")}
      >
        {sortedPhotos.length ? (
          sortedPhotos.map((photo) => {
            const photoSrc = preview && photo.preview_url ? photo.preview_url : photo.url;
            return (
              <SwiperSlide key={photo.id} className={`${slidesPerView === 1 ? "w-100" : ""} h-auto ratio ratio-${ratio}`}>
                <div className={styles.slideWrapper}>
                  {/* Blurred mirror background */}
                  <img
                    src={photoSrc}
                    alt=""
                    className={styles.mirrorBackground}
                  />
                  {/* Main media content */}
                  <LazyLoadImage
                    src={photoSrc}
                    alt=""
                    wrapperProps={{ className: styles.imageWrapper }}
                    className={styles.mainImage}
                    effect="blur"
                    placeholderSrc={photo.placeholder_url || null}
                  />
                </div>
              </SwiperSlide>
            );
          })
        ) : (
          <SwiperSlide className={`${slidesPerView === 1 ? "w-100" : ""} h-auto ratio ratio-${ratio}`}>
            <ImagePlaceholder label={false} />
          </SwiperSlide>
        )}
        {sortedPhotos.length ? (
          <>
            <div
              className="swiper-button-prev top-0 start-0 m-0 p-2 h-100 w-auto bg-white bg-opacity-0 bg-opacity-25-hover transition hover-target__show-on-hover"
              ref={slideLeftRef}
            >
              <div className="bg-dark bg-opacity-50 rounded-circle position-absolute" style={{ width: 25, height: 25 }} />
              <Icon iconName="Dropdown" width={27} height={27} className="text-white rotate-90 w-auto h-auto" />
            </div>
            <div
              className="swiper-button-next top-0 end-0 m-0 p-2 h-100 w-auto bg-white bg-opacity-0 bg-opacity-25-hover transition hover-target__show-on-hover"
              ref={slideRightRef}
            >
              <div className="bg-dark bg-opacity-50 rounded-circle position-absolute" style={{ width: 25, height: 25 }} />
              <Icon iconName="Dropdown" width={27} height={27} className="text-white rotate-270 w-auto h-auto" />
            </div>
          </>
        ) : null}
      </Swiper>
    </div>
  );
};

PhotoSlider.propTypes = {
  photos: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      url: PropTypes.string.isRequired,
      preview_url: PropTypes.string,
      placeholder_url: PropTypes.string,
    })
  ),
  cropped_photos: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      url: PropTypes.string.isRequired,
      preview_url: PropTypes.string,
      placeholder_url: PropTypes.string,
    })
  ),
  preview: PropTypes.bool,
  ratio: PropTypes.string,
  slidesPerView: PropTypes.number,
  className: PropTypes.string,
  swiperClassName: PropTypes.string,
};

export default PhotoSlider;